import { useCallback, useMemo, useState } from 'react';
import { BuildingSummary } from '../types/protocols';
import { buildingFilterOptions, matchesBuildingKey } from './buildingKeyFilter';

/**
 * Specific-building filter for list/board pages: one option per building
 * next to the type and group columns. Session only on purpose (nothing is
 * persisted), so a narrowed view never survives a reload unnoticed.
 *
 * Like `useBuildingTagFilter`, the state is the DESELECTED keys: a building
 * loaded or created later is visible until the user unchecks it.
 */
export function useBuildingKeyFilter(buildings: BuildingSummary[] | undefined) {
    const [deselectedKeys, setDeselectedKeys] = useState<ReadonlySet<string>>(() => new Set());

    const buildingOptions = useMemo(() => buildingFilterOptions(buildings), [buildings]);
    const knownKeys = useMemo(() => new Set(buildingOptions.map(option => option.key)), [buildingOptions]);

    // Deselections restricted to buildings that still exist
    const effectiveDeselected = useMemo(() => {
        const set = new Set<string>();
        deselectedKeys.forEach(key => {
            if (knownKeys.has(key)) set.add(key);
        });
        return set;
    }, [deselectedKeys, knownKeys]);

    const allBuildingsSelected = effectiveDeselected.size === 0;
    const buildingKeyFilterActive = knownKeys.size > 0 && !allBuildingsSelected;

    const isBuildingSelected = useCallback(
        (key: string): boolean => !effectiveDeselected.has(key),
        [effectiveDeselected]
    );

    const matchesBuildingKeyFilter = useCallback(
        (buildingKey: string): boolean => matchesBuildingKey(buildingKey, effectiveDeselected, knownKeys),
        [effectiveDeselected, knownKeys]
    );

    const toggleBuilding = useCallback((key: string) => {
        setDeselectedKeys(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    }, []);

    // "Alla" acts on every building, never only the rows a search shows
    const toggleAllBuildings = useCallback(() => {
        setDeselectedKeys(effectiveDeselected.size === 0 ? new Set(knownKeys) : new Set());
    }, [effectiveDeselected, knownKeys]);

    return {
        buildingOptions,
        allBuildingsSelected,
        buildingKeyFilterActive,
        isBuildingSelected,
        toggleBuilding,
        toggleAllBuildings,
        matchesBuildingKeyFilter,
    };
}
